import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { Search, Users, UserPlus, ChevronRight, ChevronLeft } from 'lucide-react';

interface Driver {
  id: string;
  name: string;
  phone: string;
  email: string;
  vehicle_plate_number: string;
  license_number: string;
  current_status: string;
  is_active: boolean;
}

interface AssignDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  routeId: string;
  routeName: string;
  assignedDriverIds: string[];
}

export function AssignDriverModal({
  isOpen,
  onClose,
  onSuccess,
  routeId,
  routeName,
  assignedDriverIds,
}: AssignDriverModalProps) {
  const { get, post } = useApi();
  const { toast } = useToast();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const debouncedSearch = useDebounce(searchQuery, 300);

  const fetchDrivers = useCallback(async (currentPage: number, search: string) => {
    setLoadingDrivers(true);
    try {
      const params: Record<string, any> = {
        page: currentPage,
        limit,
        is_active: true,
      };

      if (search) {
        params.search = search;
      }

      const response: any = await get('/api/v1/dashboard/drivers', params);

      if (response?.data?.data && Array.isArray(response.data.data)) {
        setDrivers(response.data.data);
        setTotalPages(response.data.pages || 0);
        setTotal(response.data.total || 0);
      } else if (response?.data && Array.isArray(response.data)) {
        setDrivers(response.data);
        setTotalPages(response.pages || 0);
        setTotal(response.total || 0);
      } else {
        setDrivers([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في جلب قائمة السائقين',
        variant: 'destructive',
      });
    } finally {
      setLoadingDrivers(false);
    }
  }, [get, toast]);

  // Fetch when page or search changes (only when modal is open)
  useEffect(() => {
    if (isOpen) {
      fetchDrivers(page, debouncedSearch);
    }
  }, [isOpen, page, debouncedSearch, fetchDrivers]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDriverIds([]);
      setSearchQuery('');
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);


  const handleToggleDriver = (driverId: string) => {
    setSelectedDriverIds((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleSelectAll = () => {
    const unassignedOnPage = drivers.filter(
      (d) => !assignedDriverIds.includes(d.id)
    );
    const allSelected = unassignedOnPage.every((d) =>
      selectedDriverIds.includes(d.id)
    );

    if (allSelected) {
      // Deselect only the ones on current page
      setSelectedDriverIds((prev) =>
        prev.filter((id) => !unassignedOnPage.find((d) => d.id === id))
      );
    } else {
      // Select all unassigned on current page
      setSelectedDriverIds((prev) => {
        const newIds = unassignedOnPage
          .map((d) => d.id)
          .filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleSubmit = async () => {
    if (selectedDriverIds.length === 0) {
      toast({
        title: 'تنبيه',
        description: 'الرجاء اختيار سائق واحد على الأقل',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const response: any = await post(`/api/v1/settings/routes/${routeId}/drivers`, {
        driver_profile_ids: selectedDriverIds,
      });

      const data = response?.data || response;
      const assignedCount = data?.assigned || selectedDriverIds.length;
      const skippedCount = data?.skipped || 0;

      let message = `تم تعيين ${assignedCount} سائق بنجاح`;
      if (skippedCount > 0) {
        message += `، ${skippedCount} تم تخطيهم (معينون مسبقاً)`;
      }

      toast({ title: 'تم بنجاح', description: message });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error assigning drivers:', error);
      toast({
        title: 'خطأ',
        description: error?.response?.data?.message || 'فشل في تعيين السائقين',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const unassignedOnPage = drivers.filter(
    (d) => !assignedDriverIds.includes(d.id)
  );
  const allOnPageSelected =
    unassignedOnPage.length > 0 &&
    unassignedOnPage.every((d) => selectedDriverIds.includes(d.id));

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            تعيين سائقين
          </DialogTitle>
          <DialogDescription>
            اختر السائقين لتعيينهم على المسار: {routeName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="بحث عن سائق..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pe-9"
            />
          </div>

          {/* Driver List */}
          {loadingDrivers ? (
            <LoadingSkeleton type="card" rows={4} />
          ) : drivers.length === 0 ? (
            <EmptyState
              icon={<Users className="h-10 w-10" />}
              title="لا يوجد سائقون"
              description={searchQuery ? 'لا توجد نتائج مطابقة' : 'لا يوجد سائقون متاحون'}
            />
          ) : (
            <>
              {/* Select All (only for unassigned on page) */}
              {unassignedOnPage.length > 0 && (
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all"
                      checked={allOnPageSelected}
                      onCheckedChange={handleSelectAll}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-sm font-medium cursor-pointer"
                    >
                      تحديد الكل في الصفحة
                    </label>
                  </div>
                  {selectedDriverIds.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {selectedDriverIds.length} محدد
                    </span>
                  )}
                </div>
              )}

              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {drivers.map((driver) => {
                    const isAssigned = assignedDriverIds.includes(driver.id);
                    const isSelected = selectedDriverIds.includes(driver.id);

                    return (
                      <div
                        key={driver.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          isAssigned
                            ? 'bg-muted/50 opacity-60 cursor-not-allowed'
                            : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                        onClick={() => !isAssigned && handleToggleDriver(driver.id)}
                      >
                        <Checkbox
                          checked={isSelected || isAssigned}
                          disabled={isAssigned}
                          onCheckedChange={() => handleToggleDriver(driver.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{driver.name}</p>
                            {isAssigned && (
                              <span className="text-xs bg-muted px-2 py-0.5 rounded">
                                معين مسبقاً
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span dir="ltr">{driver.phone}</span>
                            {driver.vehicle_plate_number && (
                              <span className="font-mono">
                                {driver.vehicle_plate_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-muted-foreground">
                    صفحة {page} من {totalPages} ({total} سائق)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!hasPrev || loadingDrivers}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!hasNext || loadingDrivers}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedDriverIds.length === 0}
          >
            {submitting ? 'جاري التعيين...' : `تعيين (${selectedDriverIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
